import React from 'react';
import styled from 'styled-components';

/* ── Type tokens ─────────────────────────────────────────────── */
const SERIF = "'Playfair Display', serif";
const BODY  = "'Source Serif 4', serif";
const DARK  = '#111827';   /* deep navy-charcoal for header / CTA */

/* ── Root ───────────────────────────────────────────────────── */
const Page = styled.div`
  background: #fff;
  color: #111;
`;

/* ╔══════════════════════════════════════════════════════╗
   ║  DARK HEADER  —  title, authors, buttons             ║
   ╚══════════════════════════════════════════════════════╝ */
const Header = styled.header`
  background: ${DARK};
  padding: 5rem 2rem 4rem;
  text-align: center;
`;

const HeaderInner = styled.div`
  max-width: 860px;
  margin: 0 auto;
`;


const Title = styled.h1`
  font-family: ${SERIF};
  font-size: clamp(3.5rem, 8vw, 6.5rem);
  font-weight: 900;
  color: #fff;
  line-height: 1;
  letter-spacing: -0.025em;
  margin-bottom: 1.2rem;
`;

const TitleRule = styled.hr`
  width: 52px;
  border: none;
  border-top: 1.5px solid rgba(255,255,255,0.25);
  margin: 0 auto 1.6rem;
`;

const Subtitle = styled.p`
  font-family: ${BODY};
  font-size: 1.02rem;
  color: rgba(255,255,255,0.55);
  line-height: 1.8;
  max-width: 580px;
  margin: 0 auto 2.2rem;
`;

const AuthorLine = styled.p`
  font-family: ${BODY};
  font-size: 0.86rem;
  color: rgba(255,255,255,0.5);
  line-height: 2.0;
  margin-bottom: 0.5rem;
  font-style: italic;
`;

const AffLine = styled.p`
  font-family: ${BODY};
  font-size: 0.72rem;
  color: rgba(255,255,255,0.28);
  margin-bottom: 2.6rem;
  letter-spacing: 0.01em;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const WhiteBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  background: #fff;
  color: ${DARK};
  text-decoration: none;
  padding: 0.78rem 2rem;
  font-family: ${BODY};
  font-size: 0.88rem;
  font-weight: 700;
  border: 1.5px solid #fff;
  letter-spacing: 0.01em;
  transition: background 0.18s;
  cursor: pointer;

  &:hover { background: #e8e8e8; border-color: #e8e8e8; }
`;


/* ╔══════════════════════════════════════════════════════╗
   ║  GIF TEASER STRIP — figures float on white           ║
   ╚══════════════════════════════════════════════════════╝ */
const GifStrip = styled.div`
  background: #fff;
  padding: 2.5rem 2rem 1.5rem;
  border-bottom: 1px solid #e8e8e8;
`;

const GifGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;

  @media (max-width: 860px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const GifCell = styled.div`
  background: #fff;

  img {
    width: 100%;
    height: 260px;
    object-fit: contain;
    display: block;
    background: #fff;
  }
`;

/* ╔══════════════════════════════════════════════════════╗
   ║  SECTIONS                                            ║
   ╚══════════════════════════════════════════════════════╝ */
const Section = styled.section`
  padding: 3.5rem 2rem;
  background: ${({ $alt }) => $alt ? '#f8f8f6' : '#fff'};
  border-bottom: 1px solid #ebebeb;
`;

const Inner = styled.div`
  max-width: 860px;
  margin: 0 auto;
`;

const SecNum = styled.span`
  display: block;
  font-family: ${BODY};
  font-size: 0.67rem;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #aaa;
  margin-bottom: 0.35rem;
`;

const SecHead = styled.h2`
  font-family: ${SERIF};
  font-size: clamp(1.35rem, 2.5vw, 1.75rem);
  font-weight: 800;
  color: #111;
  margin-bottom: 1.5rem;
`;

/* ── Abstract ─────────────────────────────────────────────── */
const AbstractText = styled.div`
  p {
    font-family: ${BODY};
    font-size: 1.02rem;
    line-height: 2.05;
    color: #222;
    text-align: justify;
    margin-bottom: 1.1rem;

    &:last-child { margin-bottom: 0; }
  }
`;

/* ── Applications ─────────────────────────────────────────── */
const AppRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const AppTag = styled.div`
  padding: 0.6rem 1.7rem;
  border: 1.5px solid #111;
  font-family: ${BODY};
  font-size: 0.9rem;
  font-weight: 600;
  color: #111;
  letter-spacing: 0.02em;
`;

/* ╔══════════════════════════════════════════════════════╗
   ║  DARK CTA — mirrors the header, bookends the page    ║
   ╚══════════════════════════════════════════════════════╝ */
const CtaSection = styled.section`
  background: ${DARK};
  padding: 4rem 2rem;
  text-align: center;
`;

const CtaInner = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  font-family: ${SERIF};
  font-size: clamp(1.5rem, 3vw, 2.1rem);
  font-weight: 900;
  color: #fff;
  margin-bottom: 0.7rem;
`;


const CtaBtns = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center;
`;

/* ── Data ───────────────────────────────────────────────────── */
/* Same 4 as the original page: Vrikshasana, Forward Lunge, Parshvakonasana, Pointing */
const poses = [
  '/bherigpt/images/S17_Vrikshasana_Trial1_stageii.gif',
  '/bherigpt/images/S14_ForwardLungeRight_Trial1_stageii.gif',
  '/bherigpt/images/S4_StepSideRight_Trial2_stageii.gif',
  '/bherigpt/images/S1_Point1_Trial14_stageii.gif',
];

const allAuthors = [
  { name: 'Yashaswini Mandayam Rangayyan', isLead: true  },
  { name: 'Avinash Kumar Singh',           isLead: true  },
  { name: 'Jigyasa Chand',                 isLead: true  },
  { name: 'Suvitti Suvitti',               isLead: true  },
  { name: 'Malisetti Gayathri',            isLead: false },
  { name: 'Arpit Shrimankar',              isLead: false },
  { name: 'Ankita Jain',                   isLead: false },
  { name: 'Bhuvan Koduru',                 isLead: false },
  { name: 'Anand Kaniyaar Sriranga Prasad',isLead: false },
  { name: 'Abhay Gupta',                   isLead: false },
  { name: 'Abhishek Raje',                 isLead: false },
  { name: 'Kousik Sarathy Sridharan',      isLead: false },
  { name: 'Mohan Raghavan',                isLead: false },
];

/* ── Component ──────────────────────────────────────────────── */
const BheriGPT = () => (
  <Page>

    {/* ══ DARK HEADER ════════════════════════════════════════ */}
    <Header>
      <HeaderInner>
        <Title>GATI-GPT</Title>
        <TitleRule />

        <Subtitle style={{ fontSize: '0.82rem', marginBottom: '0.8rem', color: 'rgba(255,255,255,0.38)', letterSpacing: '0.12em', textTransform: 'uppercase', lineHeight: 1.4 }}>
          Gait and Temporal Intelligence GPT
        </Subtitle>

        <Subtitle>
          A large-scale motion-capture dataset of daily activities in the Indian context.
        </Subtitle>

        <AuthorLine>
          {allAuthors.map((a, i) => (
            <React.Fragment key={a.name}>
              {i > 0 && ', '}
              {a.name}
              {a.isLead && (
                <sup style={{ fontSize: '0.6em', fontStyle: 'normal', opacity: 0.65 }}>*</sup>
              )}
            </React.Fragment>
          ))}
        </AuthorLine>
        <AffLine>Movement Intelligence Laboratory · Indian Institute of Technology Hyderabad</AffLine>

        <BtnRow>
          <WhiteBtn href="/bherigpt/pdfs/manuscript.pdf" target="_blank" rel="noopener noreferrer">
            Read Paper
          </WhiteBtn>
          <WhiteBtn href="https://github.com/AbhishekRaje2004/GATI-GPT" target="_blank" rel="noopener noreferrer">
            GitHub
          </WhiteBtn>
          <WhiteBtn href="https://zenodo.org/records/20339978" target="_blank" rel="noopener noreferrer">
            Dataset
          </WhiteBtn>
        </BtnRow>
      </HeaderInner>
    </Header>

    {/* ══ GIF TEASER — figures float immediately below header ═ */}
    <GifStrip>
      <GifGrid>
        {poses.map((src) => (
          <GifCell key={src}>
            <img src={src} alt="Motion capture teaser" loading="lazy" />
          </GifCell>
        ))}
      </GifGrid>
    </GifStrip>

    {/* ══ § 1  ABSTRACT ══════════════════════════════════════ */}
    <Section $alt>
      <Inner>
        <SecNum>§ 1 &nbsp; Abstract</SecNum>
        <SecHead>What is GATI-GPT?</SecHead>
        <AbstractText>
          <p>
            We present GATI-GPT, a high-fidelity movement dataset of daily activities performed
            by Indians. The dataset consists of 16 subjects performing various activities,
            including domestic chores, exercises, and yoga postures. We provide pose estimation
            metrics extracted from state-of-the-art pose estimation models like MotionAGFormer,
            MHFormer, PoseFormerV2, and MediaPipe on this dataset, evaluated across 4 camera
            viewing angles, along with segment length errors relative to the ground truth. We
            also provide the AMASS-compatible SMPL-H representation for our dataset, processed
            and obtained using the motion-capture system as support for this work. This dataset
            can have a significant impact in robotics, computer vision, and personalized
            rehabilitation technology by providing biomechanically grounded human movement
            sequences.
          </p>
        </AbstractText>
      </Inner>
    </Section>

    {/* ══ § 2  APPLICATIONS ══════════════════════════════════ */}
    <Section>
      <Inner>
        <SecNum>§ 2 &nbsp; Applications</SecNum>
        <SecHead>Areas of Impact</SecHead>
        <AppRow>
          <AppTag>Robotics</AppTag>
          <AppTag>Computer Vision</AppTag>
          <AppTag>Rehabilitation</AppTag>
        </AppRow>
      </Inner>
    </Section>

    {/* ══ DARK CTA — bookends the page ═══════════════════════ */}
    <CtaSection>
      <CtaInner>
        <CtaTitle>Access the Dataset</CtaTitle>
        <CtaBtns>
          <WhiteBtn href="/bherigpt/pdfs/manuscript.pdf" target="_blank" rel="noopener noreferrer">
            Read Paper
          </WhiteBtn>
          <WhiteBtn href="https://github.com/AbhishekRaje2004/GATI-GPT" target="_blank" rel="noopener noreferrer">
            GitHub
          </WhiteBtn>
          <WhiteBtn href="https://zenodo.org/records/20339978" target="_blank" rel="noopener noreferrer">
            Dataset
          </WhiteBtn>
        </CtaBtns>
      </CtaInner>
    </CtaSection>

  </Page>
);

export default BheriGPT;
